import { Fragment, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePollForUpdates } from '@frontend/automatic-updates';
import appConfig from '@frontend/env';
import { sentry } from '@frontend/tracking';

type ContextValue = {
  currentVersion: string | undefined;
  updateAvailable: boolean;
  shouldPreventUpdate: boolean;
  setShouldPreventUpdate: (value: boolean) => void;
  refresh: (type: 'hard' | 'soft', reason?: string) => void;
  hasAlreadyRefreshed: (reason?: string) => boolean;
};
const AppContext = createContext<ContextValue>({
  currentVersion: undefined,
  updateAvailable: false,
  shouldPreventUpdate: false,
  setShouldPreventUpdate: () => {},
  refresh: (_type, _reason) => {},
  hasAlreadyRefreshed: (_reason) => false,
});
export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [shouldPreventUpdate, setShouldPreventUpdate] = useState(false);

  const { updateAvailable, currentVersion } = usePollForUpdates(
    60000,
    appConfig.PUBLISH_URL ? 'https://' + appConfig.PUBLISH_URL + '/_m/version' : undefined
  );

  useEffect(() => {
    if (currentVersion) {
      localStorage.setItem('current-version', currentVersion);
    } else {
      localStorage.removeItem('current-version');
    }
  }, [currentVersion]);

  const [key, setKey] = useState(Math.random());

  // Allows resetting the app to force a remount of the entire provider tree and components.
  // Prevents refresh loops by tracking local storage keys with a one minute expiry.
  const refresh = useCallback((type: 'hard' | 'soft', reason?: string) => {
    if (!reason) {
      reason = 'unknown';
    }
    const now = Date.now();
    const keyName = `last-refresh-reason-${reason}`;

    if (!hasAlreadyRefreshed(reason)) {
      localStorage.setItem(keyName, JSON.stringify({ expiry: now }));
      if (type === 'hard') {
        sentry.error({
          error: `page refresh: ${reason}`,
          topic: 'infra',
          severityLevel: 'error',
          addContext: {
            name: 'reason',
            context: {
              windowPathname: window.location.pathname,
              reason,
            },
          },
        });
        location.reload();
      } else {
        // just updates the key to trigger a remount of the app full provider tree
        setKey(Math.random());
      }
    } else {
      sentry.error({
        error: `page refresh loop attempted: ${reason}`,
        topic: 'infra',
        severityLevel: 'error',
        addContext: {
          name: 'reason',
          context: {
            windowPathname: window.location.pathname,
            reason,
          },
        },
      });
    }
    removeExpiredRefreshReasons(reason);
  }, []);

  useEffect(() => {
    removeExpiredRefreshReasons();
  }, []);

  const value = useMemo(
    () => ({
      currentVersion,
      updateAvailable,
      shouldPreventUpdate,
      setShouldPreventUpdate,
      refresh,
      hasAlreadyRefreshed: (reason?: string) => hasAlreadyRefreshed(reason),
    }),
    [currentVersion, updateAvailable, refresh, shouldPreventUpdate, setShouldPreventUpdate]
  );
  return (
    <AppContext.Provider value={value}>
      <Fragment key={key}>{children}</Fragment>
    </AppContext.Provider>
  );
};

export const useApp = () => {
  return useContext(AppContext);
};

const getExpiry = (item: string | null) => {
  if (!item) return undefined;
  try {
    return (JSON.parse(item) as { expiry: number }).expiry;
  } catch {
    return undefined;
  }
};

// Checks if a refresh has already occurred for a given reason within the last minute
const hasAlreadyRefreshed = (reason?: string) => {
  if (!reason) reason = 'unknown';
  const keyName = `last-refresh-reason-${reason}`;
  const now = Date.now();

  const expiry = getExpiry(localStorage.getItem(keyName));
  return !!expiry && now - expiry < 60 * 1000;
};

const removeExpiredRefreshReasons = (exclude?: string) => {
  const now = Date.now();
  // Remove any refresh reasons with an expiry over one minute
  Object.keys(localStorage)
    .filter((k) => k.startsWith('last-refresh-reason-') && (exclude ? !k.endsWith(exclude) : true))
    .forEach((k) => {
      const expiry = getExpiry(localStorage.getItem(k));
      if (!expiry || now - expiry > 60 * 1000) {
        localStorage.removeItem(k);
      }
    });
};
